import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Star } from "lucide-react";
import { motion } from "framer-motion";
import TrialCTA from "@/components/TrialCTA";
import Seo from "@/components/Seo";

const STORIES_VIDEO_URL = "https://customer-assets-0z36b82j.emergentagent.net/job_train-live-better/artifacts/b4kz604k_682A9237-63FF-4C04-812F-22780768710A.MP4";

export default function SuccessStories() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/testimonials").then((r) => setItems(r.data)); }, []);
  return (
    <div>
      <Seo
        title="Success Stories — real Kore Wellbeing member journeys"
        description="Real members, real training weeks. Read how Kore Wellbeing members in the Lake District have built strength, mobility and confidence through small-group coaching."
        path="/success-stories"
      />
      <section className="container-kore pt-16 pb-8">
        <span className="eyebrow">Success Stories</span>
        <h1 className="display-1 mt-4 text-kore-forest max-w-3xl">Real people. <em className="italic text-kore-gold not-italic">Real change.</em></h1>
      </section>

      <section data-testid="stories-video" className="container-kore pt-6 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1 }}
          className="relative rounded-3xl overflow-hidden shadow-lift bg-kore-forest max-w-sm mx-auto"
        >
          <video
            className="w-full h-auto block"
            src={STORIES_VIDEO_URL}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            controls
            data-testid="stories-video-el"
          />
        </motion.div>
      </section>

      <section data-testid="stories-community" className="container-kore pb-20">
        <div className="max-w-2xl mb-10">
          <span className="eyebrow">The Kore community</span>
          <h2 className="display-2 mt-4 text-kore-forest">Faces of the club.</h2>
          <p className="mt-4 text-kore-ink/70 leading-relaxed">
            Real members. Real training weeks. This is what a small, coached gym feels like when the community becomes the reason you keep turning up.
          </p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="rounded-3xl overflow-hidden aspect-[4/5] shadow-soft"
          >
            <img src="/gallery/community-women.webp" alt="Kore Wellbeing women's coached session" loading="lazy" className="w-full h-full object-cover" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.1 }}
            className="rounded-3xl overflow-hidden aspect-[4/5] shadow-soft"
          >
            <img src="/gallery/community-mixed.webp" alt="Kore Wellbeing Zones class group" loading="lazy" className="w-full h-full object-cover" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="rounded-3xl overflow-hidden aspect-[4/5] shadow-soft"
          >
            <img src="/gallery/community-kombat.webp" alt="Kore Kombat class members" loading="lazy" className="w-full h-full object-cover" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.3 }}
            className="rounded-3xl overflow-hidden aspect-[4/5] shadow-soft"
          >
            <img src="/gallery/community-social.webp" alt="Kore Wellbeing social night" loading="lazy" className="w-full h-full object-cover" />
          </motion.div>
        </div>
      </section>

      <section className="container-kore pb-24 grid md:grid-cols-2 gap-8">
        {items.map((t) => (
          <div key={t.id} data-testid={`story-${t.id}`} className="rounded-3xl bg-white p-10 shadow-soft border border-kore-gold/10">
            <div className="flex gap-1">{Array.from({ length: t.rating }).map((_, i) => <Star key={i} size={13} className="fill-kore-gold text-kore-gold" />)}</div>
            <blockquote className="mt-6 font-display text-2xl md:text-3xl text-kore-forest font-light italic leading-tight">"{t.quote}"</blockquote>
            <div className="mt-8 flex items-center gap-4">
              {t.image_url && <img src={t.image_url} alt={t.name} className="w-14 h-14 rounded-full object-cover" />}
              <div>
                <div className="text-kore-forest font-medium">{t.name}</div>
                <div className="text-xs uppercase tracking-[0.2em] text-kore-gold">{t.role}</div>
              </div>
            </div>
          </div>
        ))}
      </section>
      <TrialCTA variant="inline" />
    </div>
  );
}
